<template>
  <app-right-sidebar
    :width="640"
    :show-sidebar="getShowSidebar"
    :form-invalid="$v.$invalid"
    :disable-button="disableButton"
    :sidebar-title="getSidebarTitle"
    title-style="center"
    buttons-style="center"
    @hideSidebar="hideSidebar($event)"
    @saveData="saveData"
  >
    <form :class="$style.addRealEstateForm">
      <div :class="$style.formMain">
        <div v-show="getPrimaryResidence || !getIsTherePrimaryResidence">
          <app-checkbox-group label="Primary Residence"
                              :class="$style.checkbox"
                              v-model="realEstate.isPrimaryResidence"
                              :value="getPrimaryResidence"
                              @input="setPrimaryResidence"/>
        </div>

        <app-checkbox-group label="Rental Income Property"
                            :class="$style.checkbox"
                            v-model="realEstate.isRentalIncomeProperty"
                            :value="getRentalIncomeProperty"
                            @input="setRentalIncomeProperty"/>

        <div :class="$forms.formRow">
          <div :class="[$forms.inputGroup, $style.inputGroup, $style.shortGroup]">
            <app-input-group v-model.trim="$v.realEstate.city.$model"
                             label="City*"
                             type="text"/>
            <p v-if="$v.realEstate.city.$error"
               :class="$forms.errorMessage">
              City is required
            </p>
          </div>

          <div :class="[$forms.inputGroup, $style.inputGroup, $style.shortGroup]">
            <app-select-group label="State*"
                              :value="getState"
                              :items="getStates"
                              v-model="$v.realEstate.stateAbbreviation.$model"
                              @input="changeState" />
            <p v-if="$v.realEstate.stateAbbreviation.$error"
               :class="$forms.errorMessage">
              State is required
            </p>
          </div>

          <div :class="[$forms.inputGroup, $style.inputGroup, $style.shortGroup]">
            <app-select-group label="County of Residence*"
                              :value="getCounty"
                              :items="getCounties"
                              v-model="$v.realEstate.countyName.$model"
                              @input="changeCounty" />
            <p v-if="$v.realEstate.countyName.$error"
               :class="$forms.errorMessage">
              County of Residence is required
            </p>
          </div>
        </div>

        <div :class="$forms.formRow">
          <div :class="[$forms.inputGroup, $style.wideGroup]">
            <app-select-group label="Property Type*"
                              :value="getResidenceType"
                              :items="getResidenceTypes"
                              v-model="$v.realEstate.residenceType.$model"
                              @input="setResidenceType" />

            <p v-if="$v.realEstate.residenceType.$error"
               :class="$forms.errorMessage">
              Property Type is required
            </p>
          </div>
        </div>

        <div :class="$forms.formRow">
          <div :class="[$forms.inputGroup, $style.inputGroup, $style.shortGroup]">
            <app-input-group v-model.trim="$v.realEstate.yearPurchased.$model"
                             label="Year Purchased*"
                             type="number"/>
            <p v-if="$v.realEstate.yearPurchased.$error"
               :class="$forms.errorMessage">
              Please, enter valid year
            </p>
          </div>

          <div :class="[$forms.inputGroup, $style.inputGroup, $style.shortGroup]">
            <app-currency-input v-model="$v.realEstate.purchasePrice.$model"
                                :value="realEstate.purchasePrice"
                                class="shortGroup"
                                label="Purchase Price*"/>
            <p v-if="$v.realEstate.purchasePrice.$error"
               :class="$forms.errorMessage">
              Purchase Price is invalid
            </p>
          </div>

          <div :class="[$forms.inputGroup, $style.inputGroup, $style.shortGroup]">
            <app-currency-input v-model="$v.realEstate.monthlyPayment.$model"
                                :value="realEstate.monthlyPayment"
                                class="shortGroup"
                                label="Monthly Payment(s)"/>
            <p v-if="$v.realEstate.monthlyPayment.$error"
               :class="$forms.errorMessage">
              Monthly Payment is invalid
            </p>
          </div>
        </div>

        <div :class="$forms.formRow">
          <div :class="[$forms.inputGroup, $style.inputGroup, $style.shortGroup]">
            <app-input-group v-model.trim="$v.realEstate.yearRefinanced.$model"
                             label="Year Refinanced"
                             type="number"/>
            <p v-if="$v.realEstate.yearRefinanced.$error"
               :class="$forms.errorMessage">
              Please, enter valid year
            </p>
          </div>

          <div :class="[$forms.inputGroup, $style.inputGroup, $style.shortGroup]">
            <app-currency-input v-model="$v.realEstate.refinancedAmount.$model"
                                :value="realEstate.refinancedAmount"
                                class="shortGroup"
                                label="Refinanced Amount"
                                :tooltip="true">
              <template v-slot:tooltipHeader>
                Real Estate Refinance
              </template>
              <template v-slot:tooltipText>
                Only enter the last Refinance Information (Year and Amount)
                if you have refinanced your property since you originally
                purchased it.
              </template>
            </app-currency-input>
            <p v-if="$v.realEstate.refinancedAmount.$error"
               :class="$forms.errorMessage">
              Refinanced Amount is invalid
            </p>
          </div>
        </div>

        <div :class="$forms.formRow">
          <div :class="[$forms.inputGroup, $style.inputGroup, $style.shortGroup]">
            <app-currency-input v-model="$v.realEstate.currentValue.$model"
                                :value="realEstate.currentValue"
                                class="shortGroup"
                                label="Current Value*"/>
            <p v-if="$v.realEstate.currentValue.$error"
               :class="$forms.errorMessage">
              Current Value is invalid
            </p>
          </div>

          <div :class="[$forms.inputGroup, $style.inputGroup, $style.shortGroup]">
            <app-currency-input v-model="$v.realEstate.balanceOwed.$model"
                                :value="realEstate.balanceOwed"
                                class="shortGroup"
                                label="Balance Owed"/>
            <p v-if="$v.realEstate.balanceOwed.$error"
               :class="$forms.errorMessage">
              Balance Owed is invalid
            </p>
          </div>
        </div>

      </div>
      <div v-if="realEstate.isRentalIncomeProperty"
           :class="$style.formBordered">
        <div :class="$style.formBorderedInner">

          <h4 :class="$style.borderedFormTitle">Rental Income Property</h4>
          <div :class="[$forms.formRow, $style.formRowSpaceBetween]">
            <h6>Rental Income
              <app-tooltip :class="$style.rentalIncomeTooltip">
                <template v-slot:tooltipHeader>
                  Rental Income
                </template>
                <template v-slot:tooltipText>
                  Total the rent collected over the last 12 months and divide by 12 for average
                  monthly rental income. If you had a 3 month vacancy, your rental income would
                  have been for 9 months only; you will still divide this amount by 12 for your
                  average monthly rental income.
                  <br><br>
                  Example: you rent out an apartment for $1,000/month but it was empty for 3 out
                  of the last 12 months, therefore you collected $9,000 for the year for an average
                  monthly income of $750.00 ($9000/12= $750)
                  <br><br>
                  There is generally a vast gap between collected rental income and net rental
                  income. It is critically important to take all expenses into consideration.
                  It is quite possible to have negative cash flow with rental properties.
                  <br><br>
                  However, if there is a negative cashflow on rental property or rental properties,
                  the IRS will only accept zero (0) as a minimum value.
                </template>
              </app-tooltip>
            </h6>
            <div :class="[$forms.inputGroup, $style.inputGroup, $style.shortGroup]">
              <app-currency-input v-model="$v.realEstate.rentalIncome.$model"
                                  :value="realEstate.rentalIncome"
                                  class="shortGroup"
                                  label=""/>
              <p v-if="$v.realEstate.rentalIncome.$error"
                 :class="$forms.errorMessage">
                Rental Income is invalid
              </p>
            </div>
          </div>

          <div :class="[$forms.formRow, $style.formRowSpaceBetween]">
            <h6>Monthly Insurance(s), advertising, utilities</h6>
            <div :class="[$forms.inputGroup, $style.inputGroup, $style.shortGroup]">
              <app-currency-input v-model="$v.realEstate.insurance.$model"
                                  :value="realEstate.insurance"
                                  class="shortGroup"
                                  label=""/>
              <p v-if="$v.realEstate.insurance.$error"
                 :class="$forms.errorMessage">
                Insurance is invalid
              </p>
            </div>
          </div>

          <div :class="[$forms.formRow, $style.formRowSpaceBetween]">
            <h6>Monthly Property Tax/Legal/ Administrative Fees</h6>
            <div :class="[$forms.inputGroup, $style.inputGroup, $style.shortGroup]">
              <app-currency-input v-model="$v.realEstate.propertyTax.$model"
                                  :value="realEstate.propertyTax"
                                  class="shortGroup"
                                  label=""/>
              <p v-if="$v.realEstate.propertyTax.$error"
                 :class="$forms.errorMessage">
                The field is invalid
              </p>
            </div>
          </div>

          <div :class="[$forms.formRow, $style.formRowSpaceBetween]">
            <h6>Monthly Cleaning/Maintenance/Repairs</h6>
            <div :class="[$forms.inputGroup, $style.inputGroup, $style.shortGroup]">
              <app-currency-input v-model="$v.realEstate.maintenance.$model"
                                  :value="realEstate.maintenance"
                                  class="shortGroup"
                                  label=""/>
              <p v-if="$v.realEstate.maintenance.$error"
                 :class="$forms.errorMessage">
                The field is invalid
              </p>
            </div>
          </div>

          <div :class="[$forms.formRow, $style.formRowSpaceBetween]">
            <h6>Monthly Management Fees/Commissions</h6>
            <div :class="[$forms.inputGroup, $style.inputGroup, $style.shortGroup]">
              <app-currency-input v-model="$v.realEstate.managementFees.$model"
                                  :value="realEstate.managementFees"
                                  class="shortGroup"
                                  label=""/>
              <p v-if="$v.realEstate.managementFees.$error"
                 :class="$forms.errorMessage">
                The field is invalid
              </p>
            </div>
          </div>

          <div :class="$style.rentalIncomeResult">
            <h6>Rental Income - Total Expenses</h6>
            <span :class="$style.netRentalIncome">{{ getNetRentalIncome | currency }}</span>
          </div>

          <p :class="$style.note">* Rental Income - Total Expenses = Net Rental Income</p>

        </div>
      </div>
    </form>

    <div v-if="serverError">
      <p :class="$forms.errorMessage">
        {{ serverError }}
      </p>
    </div>
  </app-right-sidebar>
</template>

<script>
import currency from 'currency.js';
import { mapActions, mapGetters } from 'vuex';
import {
  required, minLength, maxLength, minValue, maxValue, integer,
} from 'vuelidate/lib/validators';
import { mask } from 'vue-the-mask';
import { mustBePositive, mustBeZeroOrPositive } from '@/utils/validation';
import { RESIDENCE_TYPES } from '@/constants';

export default {
  props: {
    showSidebar: {
      type: Boolean,
      default: false,
    },
    sidebarType: {
      type: String,
      default: 'add',
    },
    editedItem: {
      type: Object,
      default: null,
    },
    editedItemId: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      serverError: '',
      realEstate: {
        isRentalIncomeProperty: false,
        isPrimaryResidence: false,
        city: '',
        stateAbbreviation: '',
        countyName: '',
        residenceType: '',
        yearPurchased: '',
        yearRefinanced: '',
        purchasePrice: 0,
        refinancedAmount: 0,
        monthlyPayment: 0,
        currentValue: 0,
        balanceOwed: 0,
        rentalIncome: 0,
        insurance: 0,
        netRentalIncome: 0,
        managementFees: 0,
        maintenance: 0,
        propertyTax: 0,
      },
      disableButton: false,
      counties: [],
      isPrimaryResidenceSaved: true,
      initialRealEstate: null,
    };
  },
  methods: {
    ...mapActions([
      'postNewRealEstate',
      'editRealEstate',
      'countiesRequest',
    ]),
    hideSidebar(e) {
      this.clearFields();
      this.isPrimaryResidenceSaved = true;
      this.$emit('hideSidebar', e);
    },
    saveData() {
      if (this.lodash.isEqual(this.initialRealEstate, this.realEstate)) {
        this.hideSidebar();
        return;
      }

      /* Disable Save button in sidebar to prevent double clicking */
      this.disableButton = true;

      const selectedCounty = this.getCountiesOfState
        .find(state => state.name === this.realEstate.countyName);

      const newItem = {
        ...this.realEstate,
        yearPurchased: +this.realEstate.yearPurchased,
        yearRefinanced: +this.realEstate.yearRefinanced,
        netRentalIncome: +this.getNetRentalIncome,
        residenceType: RESIDENCE_TYPES.find(type => type
          .textValue === this.realEstate.residenceType).code,
        countyId: selectedCounty.id,
      };

      if (this.sidebarType === 'edit') {
        newItem.id = this.editedItemId;

        this.editRealEstate({
          editedItemId: newItem.id,
          newItem,
          userId: this.getUserId,
        })
          .then(() => {
            this.hideSidebar();
          })
          .catch((err) => {
            this.serverError = err.response.data.title;
            this.disableButton = false;
          });
      } else {
        this.postNewRealEstate({
          newItem,
          userId: this.getUserId,
        })
          .then(() => {
            this.hideSidebar();
          })
          .catch((err) => {
            this.serverError = err.response.data.title;
            this.disableButton = false;
          });
      }
    },
    clearFields() {
      Object.keys(this.realEstate).forEach((key) => {
        this.realEstate[key] = '';
      });
      this.realEstate.purchasePrice = 0;
      this.realEstate.refinancedAmount = 0;
      this.realEstate.monthlyPayment = 0;
      this.realEstate.currentValue = 0;
      this.realEstate.balanceOwed = 0;
      this.realEstate.rentalIncome = 0;
      this.realEstate.insurance = 0;
      this.realEstate.netRentalIncome = 0;
      this.realEstate.managementFees = 0;
      this.realEstate.maintenance = 0;
      this.realEstate.propertyTax = 0;
      this.realEstate.isPrimaryResidence = false;
      this.realEstate.isRentalIncomeProperty = false;
      this.counties = [];
      this.$v.$reset();
      this.disableButton = false;
    },
    setFormFields() {
      this.countiesRequest(this.editedItem.stateAbbreviation)
        .then(() => {
          this.getCountiesOfState.forEach(item => this.counties.push(item.name));

          const county = this.getCountiesOfState
            .find(item => item.id === this.editedItem.countyId);

          Object.keys(this.realEstate).forEach((key) => {
            this.realEstate[key] = this.editedItem[key];
          });
          this.realEstate.countyName = county.name;
          this.realEstate.yearPurchased = this.realEstate.yearPurchased.toString();
          if (this.realEstate.yearRefinanced === 0) {
            this.realEstate.yearRefinanced = '';
          } else {
            this.realEstate.yearRefinanced = this.realEstate.yearRefinanced.toString();
          }
          this.realEstate.isRentalIncomeProperty = this.editedItem.isRentalIncomeProperty;
          this.realEstate.isPrimaryResidence = this.editedItem.isPrimaryResidence;
          this.realEstate.residenceType = RESIDENCE_TYPES.find(type => type
            .code === this.editedItem.residenceType).textValue;

          this.initialRealEstate = { ...this.realEstate };
        });
    },
    resetServerErrors() {
      this.serverError = '';
    },
    /* Change the value of the checkbox input */
    setPrimaryResidence(val) {
      this.realEstate.isPrimaryResidence = !!val;
      this.isPrimaryResidenceSaved = false;
    },
    /* Change the value of the checkbox input */
    setRentalIncomeProperty(val) {
      this.realEstate.isRentalIncomeProperty = !!val;
    },
    /* Change the value of the select */
    setResidenceType(val) {
      this.realEstate.residenceType = val;
    },
    /* Change the value of the select */
    changeState(state) {
      this.realEstate.countyName = '';
      this.realEstate.stateAbbreviation = state;
    },
    /* Change the value of the select */
    changeCounty(county) {
      this.realEstate.countyName = county;
    },
    /* Change the value of the select */
    changeCity(city) {
      this.realEstate.city = city;
    },
    fetchCountiesOfState(state) {
      this.countiesRequest(state)
        .then(() => {
          this.counties = [];
          this.getCountiesOfState.forEach(item => this.counties.push(item.name));
        });
    },
  },
  computed: {
    ...mapGetters([
      'getUserId',
      'getRealEstate',
      'getStates',
      'getCountiesOfState',
    ]),
    getResidenceTypes() {
      return RESIDENCE_TYPES.map(type => type.textValue).sort();
    },
    getShowSidebar() {
      return this.showSidebar;
    },
    getSidebarTitle() {
      return this.sidebarType === 'edit' ? 'Edit Real Estate' : 'Add New Real Estate';
    },
    /* Get the current value of the checkbox input */
    getPrimaryResidence() {
      return this.realEstate.isPrimaryResidence;
    },
    /* Get the current value of the checkbox input */
    getRentalIncomeProperty() {
      return this.realEstate.isRentalIncomeProperty;
    },
    /* Calculate Net Rental Income */
    getNetRentalIncome() {
      const totalExpenses = currency(this.realEstate.insurance)
        .add(currency(this.realEstate.propertyTax))
        .add(currency(this.realEstate.maintenance))
        .add(currency(this.realEstate.managementFees))
        .add(currency(this.realEstate.monthlyPayment));
      return currency(this.realEstate.rentalIncome).subtract(totalExpenses);
    },
    /* Get residenceType for displaying in table */
    getResidenceType() {
      return this.realEstate.residenceType;
    },
    getIsTherePrimaryResidence() {
      return this.isPrimaryResidenceSaved
        && this.getRealEstate.find(realEstate => realEstate.isPrimaryResidence === true);
    },
    /* Get the current value of the select field */
    getState() {
      if (this.realEstate.stateAbbreviation) {
        this.fetchCountiesOfState(this.realEstate.stateAbbreviation);
      }
      return this.realEstate.stateAbbreviation;
    },
    /* Get the current value of the select field */
    getCounty() {
      return this.realEstate.countyName;
    },
    /* Get the current value of the select field */
    getCity() {
      return this.realEstate.city;
    },
    getCounties() {
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      return this.counties.sort();
    },
  },
  directives: {
    currency,
    mask,
  },
  watch: {
    getShowSidebar() {
      if (this.showSidebar) {
        this.resetServerErrors();

        if (this.sidebarType === 'edit') {
          this.setFormFields();
        }
      }
    },
  },
  validations: {
    realEstate: {
      city: {
        required,
      },
      stateAbbreviation: {
        required,
      },
      countyName: {
        required,
      },
      residenceType: {
        required,
      },
      yearPurchased: {
        required,
        integer,
        minLength: minLength(4),
        maxLength: maxLength(4),
        minValue: minValue(1900),
        maxValue: maxValue(new Date().getFullYear()),
      },
      yearRefinanced: {
        integer,
        minLength: minLength(4),
        maxLength: maxLength(4),
        minValue: minValue(1900),
        maxValue: maxValue(new Date().getFullYear()),
      },
      purchasePrice: {
        required,
        mustBePositive,
      },
      monthlyPayment: {
        mustBeZeroOrPositive,
      },
      refinancedAmount: {
        mustBeZeroOrPositive,
      },
      currentValue: {
        required,
        mustBePositive,
      },
      balanceOwed: {
        mustBeZeroOrPositive,
      },
      rentalIncome: {
        mustBeZeroOrPositive,
      },
      insurance: {
        mustBeZeroOrPositive,
      },
      propertyTax: {
        mustBeZeroOrPositive,
      },
      maintenance: {
        mustBeZeroOrPositive,
      },
      managementFees: {
        mustBeZeroOrPositive,
      },
    },
  },
};
</script>

<style src="@/styles/forms.scss" lang="scss" module="$forms"></style>
<style lang="scss" module>
  @import "@/styles/variables.scss";

  .addRealEstateForm {
    display: flex;
    flex-direction: column;
    margin-top: 36px;
    margin-right: -40px;

    .checkbox {
      margin-top: -10px;
    }

    .inputGroup {
      width: 268px;

      &.shortGroup {
        width: 170px;
        margin-right: 24px;

        &:last-child {
          margin-right: 0;
        }
      }
    }

    .formBordered {
      box-shadow: $forms-shadow;
      padding: 8px;
      margin-top: 8px;

      .formBorderedInner {
        border: $contacts-border;
        padding: 16px 24px 22px;
      }

      .borderedFormTitle {
        font-family: $main-font;
        color: $table-data-color;
        opacity: 0.5;
        font-size: 1.2rem;
        line-height: 4rem;
        font-weight: 700;
        text-transform: uppercase;
      }

      .formRowSpaceBetween {
        justify-content: space-between;
        flex-wrap: nowrap;

        h6 {
          margin-right: 10px;
        }

        .rentalIncomeTooltip {

          & > span {
            top: -300px;
            left: 20px;
            width: 400px;
          }
        }
      }

      .rentalIncomeResult {
        border-top: $result-border;
        margin-top: 20px;
        padding-top: 15px;
        display: flex;
        justify-content: space-between;

        .netRentalIncome {
          padding: 0 16px;
        }
      }

      .note {
        color: $light-text-color;
        font-size: 1.3rem;
        line-height: 2.1rem;
        font-style: italic;
      }
    }
  }

  .wideGroup {
    width: 100%;
    padding-right: 40px;
  }
</style>

<style lang="scss">
  .customInputGroup.shortGroup {

    .customFormInput.v-text-field > .v-input__control > .v-input__slot {

      .v-text-field__slot {

        .v-text-field__prefix + input {
          width: 170px;
        }
      }
    }
  }
</style>
